import { useState, useEffect, useCallback } from 'react';
import { menuSchedule } from 'data/data';

interface RestaurantStatus {
  text: 'Open' | 'Closed';
  color: string;
  isOpen: boolean;
}

interface UseRestaurantStatusProps {
  menuScheduleData: menuSchedule[] | null;
  updateInterval?: number; // in milliseconds, default 60000 (1 minute)
}

// Utility functions that can be used independently
export const isOpenNow = (fromTime: string, toTime: string): boolean => {
  if (!fromTime || !toTime) return false;

  const now = new Date();
  const [fromHours, fromMinutes] = fromTime.split(":").map(Number);
  const [toHours, toMinutes] = toTime.split(":").map(Number);

  const fromDate = new Date(now);
  fromDate.setHours(fromHours, fromMinutes, 0, 0);

  const toDate = new Date(now);
  toDate.setHours(toHours, toMinutes, 0, 0);

  // Handle overnight shift (e.g., 22:00 to 02:00)
  if (toDate <= fromDate) {
    toDate.setDate(toDate.getDate() + 1);
  }

  return now >= fromDate && now <= toDate;
};

const DAYS_OF_WEEK = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export const getCurrentDay = (): string => {
  const currentDayIndex = new Date().getDay();
  return DAYS_OF_WEEK[currentDayIndex];
};

function dayNameToIndex(day: string): number {
  const i = DAYS_OF_WEEK.indexOf(day as (typeof DAYS_OF_WEEK)[number]);
  return i;
}

/** True when closing time is "before" opening time on the same calendar date (e.g. 22:00 → 02:00). */
function isOvernightWindow(fromTime: string, toTime: string): boolean {
  const [fh, fm] = fromTime.split(":").map(Number);
  const [th, tm] = toTime.split(":").map(Number);
  const fromM = fh * 60 + fm;
  const toM = th * 60 + tm;
  return fromM > toM;
}

/**
 * For a schedule on `menuDay` with overnight hours, the open window crosses midnight into the next day.
 * Example: Monday 22:00–02:00 is open Monday 22:00 through Tuesday 02:00 — so Tuesday 01:00 is still open
 * even though `getCurrentDay()` is Tuesday and there is no Tuesday row in the schedule.
 */
function isOpenDuringOvernightSpan(schedule: menuSchedule, now: Date): boolean {
  if (!isOvernightWindow(schedule.fromTime, schedule.toTime)) return false;
  const startDow = dayNameToIndex(schedule.menuDay);
  if (startDow < 0) return false;

  const [fh, fm] = schedule.fromTime.split(":").map(Number);
  const [th, tm] = schedule.toTime.split(":").map(Number);

  const windowStart = new Date(now.getTime());
  const diffDays = (now.getDay() - startDow + 7) % 7;
  windowStart.setDate(windowStart.getDate() - diffDays);
  windowStart.setHours(fh, fm, 0, 0);

  const windowEnd = new Date(windowStart.getTime());
  windowEnd.setDate(windowEnd.getDate() + 1);
  windowEnd.setHours(th, tm, 0, 0);

  return now >= windowStart && now <= windowEnd;
}

export const calculateRestaurantStatus = (menuScheduleData: menuSchedule[] | null): RestaurantStatus => {
  const currentDay = getCurrentDay();
  if (!menuScheduleData) return { text: "Closed", color: "red", isOpen: false };

  const now = new Date();

  const todaySchedules = menuScheduleData.filter(
    (schedule) => schedule.menuDay === currentDay
  );

  const isCurrentlyOpen =
    todaySchedules.some((schedule) => isOpenNow(schedule.fromTime, schedule.toTime)) ||
    menuScheduleData.some((schedule) => isOpenDuringOvernightSpan(schedule, now));

  return {
    text: isCurrentlyOpen ? "Open" : "Closed",
    color: isCurrentlyOpen ? "#7BB542" : "red",
    isOpen: isCurrentlyOpen,
  };
};

export const useRestaurantStatus = ({ 
  menuScheduleData, 
  updateInterval = 60000 
}: UseRestaurantStatusProps): RestaurantStatus => {
  const [status, setStatus] = useState<RestaurantStatus>({ text: 'Closed', color: 'red', isOpen: false });

  const calculateStatus = useCallback((): RestaurantStatus => {
    return calculateRestaurantStatus(menuScheduleData);
  }, [menuScheduleData]);

  // Update status immediately when component mounts or menuScheduleData changes
  useEffect(() => {
    setStatus(calculateStatus());
  }, [calculateStatus]);

  // Set up interval to check status periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setStatus(calculateStatus());
    }, updateInterval);

    return () => clearInterval(interval);
  }, [calculateStatus, updateInterval]);

  return status;
};